import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, Sparkles, ShieldCheck, Truck, Heart } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import CustomOrderSection from "@/components/CustomOrderSection";

const HERO_IMG = "https://customer-assets.emergentagent.com/job_yarn-market-in/artifacts/tt0cx4j9_ChatGPT%20Image%20Jun%2013%2C%202026%2C%2001_45_47%20PM.png";
const CAT_IMGS = {
  "home-decors": HERO_IMG,
  "accessories": HERO_IMG,
};

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true, limit: 8 } }).then((r) => setFeatured(r.data));
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 pt-10 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-6 relative z-10">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-marigold/20 text-deepbrown text-xs font-bold uppercase tracking-widest">
              <Sparkles className="h-3 w-3 text-rani" /> Handmade in India
            </span>
            <h1 className="font-display font-bold text-deepbrown mt-5 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Stitched with <span className="text-rani">love.</span><br />
              Worn with <span className="text-marigold">joy.</span>
            </h1>
            <p className="mt-6 text-base lg:text-lg text-deepbrown/70 max-w-lg leading-relaxed">
              Discover hand-knit & crocheted treasures from artisan makers across India.
              Cozy sweaters, cuddly amigurumi, festive shawls and more — every piece is one of a kind.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/shop"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-rani text-white font-bold shadow-lg hover:scale-105 transition-transform"
                data-testid="hero-shop-btn"
              >
                Shop the Collection <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/shop?category=home-decors"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white border-2 border-deepbrown text-deepbrown font-bold hover:bg-deepbrown hover:text-ivory transition-colors"
                data-testid="hero-amigurumi-btn"
              >
                Home Decors
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-6 text-sm text-deepbrown/70">
              <div className="flex items-center gap-2"><Truck className="h-4 w-4 text-emerald" /> Free shipping over ₹999</div>
              <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald" /> Razorpay secured</div>
              <div className="flex items-center gap-2"><Heart className="h-4 w-4 text-emerald" /> 100% handmade</div>
            </div>
          </div>

          <div className="lg:col-span-6 relative">
            <div className="relative aspect-square rounded-3xl overflow-hidden shadow-2xl">
              <img src={HERO_IMG} alt="Colorful handmade crochet" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-tr from-deepbrown/20 via-transparent to-transparent" />
            </div>
            <div className="absolute -top-6 -right-6 hidden md:flex h-32 w-32 rounded-full bg-marigold items-center justify-center font-display text-deepbrown font-bold text-lg rotate-12 shadow-xl animate-float">
              Festive<br />Picks ✨
            </div>
            <div className="absolute -bottom-6 -left-6 hidden md:flex flex-col items-center justify-center h-28 w-28 rounded-full bg-emerald text-white font-display font-bold rotate-[-8deg] shadow-xl animate-float" style={{ animationDelay: "1s" }}>
              <span className="text-2xl">100%</span>
              <span className="text-[10px] uppercase tracking-widest">Handmade</span>
            </div>
          </div>
        </div>

        {/* Marquee ribbon */}
        <div className="bg-deepbrown text-ivory py-4 overflow-hidden">
          <div className="flex animate-marquee whitespace-nowrap gap-16 font-display text-2xl">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="flex items-center gap-16">
                <span>🧶 Hand-knit with love</span>
                <span>·</span>
                <span className="text-marigold">Crafted in India</span>
                <span>·</span>
                <span>Free Shipping ₹999+</span>
                <span>·</span>
                <span className="text-rani">Festive Edit Live</span>
                <span>·</span>
                <span>Custom orders welcome</span>
                <span>·</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-rani font-bold">Browse by category</p>
            <h2 className="font-display font-bold text-deepbrown text-3xl sm:text-4xl lg:text-5xl mt-2">
              Find your <span className="text-marigold">vibe</span>
            </h2>
          </div>
          <Link to="/shop" className="hidden md:inline-flex items-center gap-1 text-deepbrown font-semibold hover:text-rani" data-testid="see-all-categories">
            See all <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {categories.map((c) => (
            <Link
              key={c.id}
              to={`/shop?category=${c.id}`}
              className="group relative aspect-[4/5] rounded-2xl overflow-hidden bg-deepbrown/5 hover:-translate-y-1 transition-transform shadow-sm hover:shadow-xl"
              data-testid={`category-card-${c.id}`}
            >
              <img src={CAT_IMGS[c.id]} alt={c.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-deepbrown/80 via-deepbrown/20 to-transparent" />
              <div className="absolute bottom-3 left-3 right-3">
                <p className="font-display text-white text-lg leading-tight">{c.name}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 pb-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-rani font-bold">Featured this week</p>
            <h2 className="font-display font-bold text-deepbrown text-3xl sm:text-4xl lg:text-5xl mt-2">
              Sutrakosh <span className="text-rani">favorites</span>
            </h2>
          </div>
          <Link to="/shop" className="hidden md:inline-flex items-center gap-1 text-deepbrown font-semibold hover:text-rani">
            See all <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {featured.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      {/* STORY BAND */}
      <section className="bg-marigold/20 py-20 relative overflow-hidden">
        <div className="max-w-5xl mx-auto px-4 md:px-8 lg:px-12">
          <p className="text-xs uppercase tracking-[0.3em] text-rani font-bold text-center">Meet the maker</p>
          <h3 className="font-display font-bold text-deepbrown text-3xl sm:text-4xl lg:text-5xl mt-3 text-center">
            Hello, I'm <span className="text-rani">Chanchala</span>.
          </h3>

          <div className="mt-10 grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-4 order-1 lg:order-1">
              <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-xl bg-white">
                <img src={HERO_IMG} alt="Sutrakosh by Chanchala" className="w-full h-full object-cover" />
              </div>
              <p className="mt-4 text-center font-display text-lg text-deepbrown italic">
                "Handcrafted with Love,<br />One Stitch at a Time."
              </p>
            </div>

            <div className="lg:col-span-8 order-2 lg:order-2 space-y-4 text-deepbrown/85 leading-relaxed text-[15px] sm:text-base">
              <p>Hello and welcome to <b>Sutrakosh by Chanchala</b>.</p>
              <p>
                My name is Chanchala, and I am the creator behind every handmade item you see here.
                What started as a passion for crochet and knitting has grown into a small business that
                allows me to share my creativity with people who appreciate handcrafted work.
              </p>
              <p>
                I work on every product myself — from selecting materials and designing patterns to
                crocheting, finishing, and carefully preparing each item for delivery. Whether it is a
                bouquet of crochet flowers, a handmade bag, home décor, curtain accessories, or a
                custom-made piece, every creation is made with patience, attention to detail, and love.
              </p>
              <p>
                I believe that handmade products carry a special warmth and personality that cannot be
                found in mass-produced items. Each stitch reflects the time, care, and dedication I put
                into my craft. My goal is to create beautiful, unique pieces that bring joy to homes and
                make meaningful gifts for loved ones.
              </p>
              <p>
                As a one-person business, every order means a great deal to me. Your support helps me
                continue doing what I love while preserving the art of handmade craftsmanship.
              </p>
              <p>
                Thank you for visiting my shop and supporting handmade creations. I am honored to be a
                part of your special moments and look forward to creating something beautiful for you.
              </p>
              <p className="font-display text-deepbrown text-xl pt-2">
                <span className="text-rani">✦</span> Handcrafted with Love, One Stitch at a Time.
              </p>
            </div>
          </div>
        </div>
      </section>

      <CustomOrderSection />
    </div>
  );
}
